'use client';

import Image from 'next/image';
import styles from './ContestSection.module.scss';
import Text from '@components/atoms/Text';
import Button from '@components/atoms/Button';
import { contestSectionData } from './ContestSection.data';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useMemo } from 'react';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { getLoginUrl } from '@utils/getLoginUrl';
import { useWishlist } from '@context/WishlistContext';
import WishlistGIF from '@components/atoms/WishlistGIF';
import { formatCompleteDate, formatDateShort, getMonthFromDate } from '@helpers/dateFormatter';
import { getContestUI, isParticipatedAndEntriesClosed } from '@helpers/contestLogic';
import { Modal } from '@components/atoms/Modal';
import { PAGE_TYPES } from '@constants/pageTypes';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import assetLoader from '@utils/internalImageLoder';
import { usePersistor } from '@lib/redux/PersistorContext';
import { useAppDispatch } from '@lib/redux/hooks';
import { clearProfile } from '@lib/redux/slices/profileSlice';
import { closeWebView } from '@hooks/useWebviewCallback';
import { useIsMobile } from '@hooks/use-mobile';
import { contestStates, postCampaignOptIn } from '@services/contestService';

const ContestSection = ({ contestState, isLoggedIn, campaignData, upcomingCampaignData, cityName, campaignOptInData, userId, campaign_stage, car_policyholder }: any) => {

  const { wishlistItems } = useWishlist();
  const campaignWishlistCount = wishlistItems.length;

  const dispatch = useAppDispatch();
  const router = useRouter();
  const persistor = usePersistor();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [failureModal, setFailureModal] = useState<boolean>(false);
  const [showParticipatedModal, setShowParticipatedModal] = useState<boolean>(false);
  const isMobile = useIsMobile();

  let hasParticipated = (contestState === "ALREADY_PARTICIPATED" || contestState === "NEW_PARTICIPANT") && campaignOptInData;

  const contestMonth = useMemo(() => {
    return getMonthFromDate(campaignData?.metadata?.productRevealDate);
  }, [campaignData?.metadata?.productRevealDate]);

  const winnerState = useMemo(() => {
    if (!campaignData?.metadata?.productRevealDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const revealDate = new Date(campaignData.metadata.productRevealDate);
    revealDate.setHours(0, 0, 0, 0);

    return revealDate.getTime() <= today.getTime();
  }, [campaignData?.metadata?.productRevealDate]);

  const uiConfig = useMemo(() => {
    return getContestUI(campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData);
  }, [campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData]);

  const isContestClosedWithNoUpcoming = useMemo(() => {
    return !upcomingCampaignData && (contestState === "NO_ACTIVE_CAMPAIGN" || contestState === "COMPLETED");
  }, [upcomingCampaignData, contestState]);

  const isAlreadyParticipatedAndEntriesClosed = useMemo(() => {
    return isParticipatedAndEntriesClosed(contestState, campaignData?.endDate);
  }, [contestState, campaignData?.endDate]);

  useEffect(() => {
    if (contestState === "NO_ACTIVE_POLICY" || contestState === "POLICY_EXPIRED") {
      setFailureModal(true);
    }
  }, [contestState])

  useEffect(() => {
    if (failureModal) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [failureModal]);

  const getButtonText = () => {
    if (uiConfig.buttonDisabled) {
      const dateToShow = uiConfig.useUpcomingDates
        ? upcomingCampaignData?.startDate
        : campaignData?.metadata?.winnerRevealDate;
      return dateToShow ? `${uiConfig.buttonText}${formatDateShort(dateToShow)}` : uiConfig.buttonText;
    }
    return uiConfig.buttonText;
  };

  const handleEnterContest = async () => {
    if (uiConfig.buttonDisabled) {
      return;
    }

    if (isLoggedIn) {
      showLoader();
      if (!campaignOptInData) {
        await postCampaignOptIn(campaignData?.id, userId);
        const states = await contestStates(userId);
        hasParticipated = states === "ALREADY_PARTICIPATED";
      }
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_wishlist_cta",
          city_name: cityName,
          campaign_stage: campaign_stage, 
          car_policyholder: car_policyholder,
          campaign_name: "car_drop"
        },
      });
      
      if (hasParticipated) {
        hideLoader();
        setShowParticipatedModal(true);
        return;
      }

      router.push("/campaign-cars/");
    } else {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
            action_type: "tap_btn",
            action_name: "tap_btn_check_eligibility",
            city_name: cityName,
            campaign_name: "car_drop"
        },
      });
      window.location.href = getLoginUrl(window.location.href);
    }
  };

  const handleFailureContest = async () => {
    showLoader();
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_explore_ad",
        city_name: cityName,
        campaign_stage: "Not eligible", 
        car_policyholder: "No",
        campaign_name: "car_drop"
      },
    });
    router.push("/")
  };

  const handleTryAgain = async () => {
    try {
      showLoader();

      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_try_again",
          city_name: cityName,
          campaign_stage: "Not eligible", 
          car_policyholder: "No",
          campaign_name: "car_drop"
        },
      });

      const basePath = process.env.NEXT_PUBLIC_BASE_TERM
        ? `${process.env.NEXT_PUBLIC_BASE_TERM}`
        : "";

      const logoutUrl = `/${basePath}api/auth/logout?redirect=${encodeURIComponent(
        window.location.pathname
      )}`;

      const response = await fetch(logoutUrl);
      const result = await response.json();

      if (!result.success) {
        console.error("Logout API error:", result.error);
      }

      dispatch(clearProfile());
      await persistor.flush();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      window.location.href = getLoginUrl(window.location.href);
    }
  }
  const refreshPage = async() => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
            action_type: "tap_btn",
            action_name: "participation_success",
            city_name: cityName,
            campaign_name: "car_drop"
        },
    });
    window.location.reload();
  }

  const handleTermsLink = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
            action_type: "tap_btn",
            action_name: "tap_btn_car_drop_tnc",
            city_name: cityName,
            campaign_stage: campaign_stage, 
            car_policyholder: car_policyholder,
            campaign_name: "car_drop"
        },
    });
  }

  return (
    <div className={styles.contestSectionContainer}>

      {
        uiConfig.showSteps && (
          <Text className={styles.wishlistHelperHeading}>What you need to do?</Text>
        )}

      {
        uiConfig.showSteps && (
          <div className={styles.stepsContainer}>
            {contestSectionData.NeedToDo.steps.map((step, index) => (
              <div key={index} className={styles.stepItem}>
                <div className={styles.stepIconWrapper}>
                  {
                    step.icon.includes("contestWishlist") ?
                      (
                        <WishlistGIF
                          width={28}
                          height={28}
                        />
                      ) : (
                        <Image
                          src={`${assetLoader()}${step.icon}`}
                          alt={step.alt}
                          width={28}
                          height={28}
                        />
                      )
                  }
                </div>
                <Text className={styles.stepLabel}>
                  {step.label}
                </Text>
              </div>
            ))}
          </div>
        )
      }

      {uiConfig.showDateReveal && (
        <div className={styles.contestRevealContainer}>
          <Image
            src={`${assetLoader()}/images/ContestPattern.png`}
            alt="Contest Pattern"
            fill
            loading="lazy"
            className={styles.ContestPatternImage}
          />

          {!isAlreadyParticipatedAndEntriesClosed && (
            <div className={uiConfig.showFullDateDetails ? styles.contestRevealDates : styles.entriesClosedRevealDates}>
              {uiConfig.showFullDateDetails ? (
                <>
                  <Text className={styles.contestRevealHeading}>The big Car Drop happens on</Text>
                  <Text className={styles.contestRevealDate}>
                    {campaignData?.metadata?.productRevealDate && formatDateShort(campaignData.metadata.productRevealDate)}
                  </Text>
                  <div className={styles.ContestDataContainer}>
                    <div className={styles.dateRevealCard}>
                      <div className={styles.dateRevealContent}>
                        <div className={styles.dateRevealItem}>
                          <Text className={styles.dateRevealLabel}>
                            Entries open
                          </Text>
                          <Text className={styles.dateRevealDate}>
                            {campaignData?.startDate && formatDateShort(campaignData.startDate)}
                          </Text>
                        </div>

                        <Image
                          src={`${assetLoader()}/icons/contestDateDivider.svg`}
                          alt="Divider"
                          fill
                          className={styles.dateDivideIcon}
                        />

                        <div className={styles.dateRevealItem}>
                          <Text className={styles.dateRevealLabel}>
                            Entries close
                          </Text>
                          <Text className={styles.dateRevealDate}>
                            {campaignData?.endDate && formatDateShort(campaignData.endDate)}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.entriesClosedRevealDatesContianer}>
                  <Text className={styles.entriesCloseDate}>
                    Entries closes on {campaignData?.endDate && formatDateShort(campaignData.endDate)}
                  </Text>
                </div>
              )
              }
            </div>
          )}

          <div className={uiConfig.showFullDateDetails ? styles.WinningAnnouncementContainer : styles.entriesCloseWinningAnnouncementContainer}>
            <Image
              src={`${assetLoader()}/icons/CarDropRevealDate.svg`}
              alt="CarDropRevealDate"
              width={64}
              height={64}
            />
            <div className={styles.WinningAnnouncementContent}>
              <Text className={styles.WinningAnnouncementHeading}>Winner announcement</Text>
              <Text className={styles.WinningAnnouncementDate}>
                {campaignData?.metadata?.winnerRevealDate && formatCompleteDate(campaignData.metadata.winnerRevealDate)}
              </Text>
            </div>
          </div>
        </div>
      )}

      <div className={styles.stepsDescriptionContainer}>
        <Text className={styles.stepsDescriptionHeading}>Drop everything else!<br />ACKO Drive Car Drop is here.</Text>
        <Image
          src={`${assetLoader()}/images/${isMobile ? "AboutADCarDropNew" : "AboutADCarDropDesktopNew"}.svg`}
          alt="About AD Car Drop"
          fill
          loading='lazy'
          className={styles.AboutADCarDrop}
        />
      </div>

      {!isContestClosedWithNoUpcoming && uiConfig.showButton && (
        <div className={styles.modalContainer}>
          <Button
            className={`${styles.contestButton} ${uiConfig.buttonDisabled ? styles.notifyButton : ''}`}
            onClick={handleEnterContest}
          >
            {getButtonText()}
          </Button>
          {uiConfig.showDisclaimer && (
            <div className={styles.disclaimerContainer}>
              <Text className={styles.disclaimerText}>
                By participating, you agree to our {" "}
                <a
                  href="/car-drop/terms-and-conditions/"
                  className={styles.disclaimerLink}
                  onClick={handleTermsLink}
                >
                  Terms and conditions
                </a>
              </Text>
            </div>
          )}
        </div>
      )}

      {showParticipatedModal && (
        <Modal
          isOpen={showParticipatedModal}
          onClose={() => refreshPage()}
          overlayClassName={styles.finalModalOverlay}
          contentClassName={styles.finalModalContent}
          headerClassName={styles.finalModalHeaderClassName}
          showCloseButton={false}
        >
          <div className={styles.CampaignFinalModalContainer}>
            <Image
              src={`${assetLoader()}/icons/Close_cross.svg`}
              alt="Close_cross"
              width={24}
              height={24}
              className={styles.CampaignFinalModalT1Close}
              onClick={() => refreshPage()}
            />
            <Image
              src={`${assetLoader()}/images/CarDropLogo.svg`}
              alt="CarDropLogo"
              width={116}
              height={24}
              loading="eager"
              className={styles.CarDropLogoFinalModal}
            />
            <div className={styles.CampaignFinalModalT1Container}>
              <Text className={styles.CampaignFinalModalT1}>3 cars wishlisted!</Text>
              <Image
                src={`${assetLoader()}/icons/campaignStart.svg`}
                alt="campaignStart"
                width={24}
                height={24}
                className={styles.campaignStartIcon}
              />
            </div>
            <Text className={styles.CampaignFinalModalT2}>
              You&apos;re officially part of our {contestMonth} Car Drop
            </Text>
            {
              winnerState ?
                <Text className={styles.CampaignFinalModalT3}>
                  Winner announcement on <span className={styles.CampaignFinalModalSpan}>
                    {campaignData?.metadata?.winnerRevealDate && formatDateShort(campaignData.metadata.winnerRevealDate)}
                  </span>
                </Text>
                :
                <Text className={styles.CampaignFinalModalT3}>
                  The big Car Drop happens on <span className={styles.CampaignFinalModalSpan}>
                    {campaignData?.metadata?.productRevealDate && formatDateShort(campaignData.metadata.productRevealDate)}
                  </span>
                </Text>
            }
            <ul className={styles.CampaignFinalModalT4Container}>
              <li className={styles.CampaignFinalModalT4List}>Your wishlist qualifies you for the Car Drop.</li>
              <li className={styles.CampaignFinalModalT4List}>Giveaway car(s) may vary from your wishlist.</li>
              <li className={styles.CampaignFinalModalT4List}>ACKO Drive covers the full on-road cost — registration and road tax included. Any additional taxes on the prize value are payable by the winner.</li>
            </ul>
            <Button className={styles.CampaignFinalModalButton} onClick={() => refreshPage()}>
              Got it
            </Button>
          </div>
        </Modal>
      )}

      {
        failureModal && (
          <div className={styles.failureModalContianer}>
            <div className={styles.failureModalHeader}>
              <Image
                src={`${assetLoader()}/icons/AckoDriveLogo.svg`}
                alt="AckoDriveLogo"
                width={72}
                height={24}
              />
              <Image
                src={`${assetLoader()}/icons/Close_cross.svg`}
                alt="AckoDriveLogo"
                width={24}
                height={24}
                onClick={() => closeWebView(() => router.back())}
              />
            </div>

            {
              contestState === "NO_ACTIVE_POLICY" && (
                <>
                  <div className={styles.failureModalContentContainer}>
                    <div className={styles.failureContent}>
                      <Image
                        src={`${assetLoader()}/icons/NO_ACTIVE_POLICY.svg`}
                        alt="NO_ACTIVE_POLICY"
                        width={88}
                        height={88}
                      />
                      <Text className={styles.failureContentHeading}>
                        We checked under the hood.<br />No policy found.
                      </Text>
                      <Text className={styles.failureContentSubHead}>
                        Car Drop is only for ACKO car policyholders.
                      </Text>
                      <div className={styles.dividedLine}></div>
                      <Text className={styles.differentNumber}>Think you used a different number? <span onClick={handleTryAgain} >Try again</span></Text>
                    </div>
                    <Text className={styles.failureTroubleEmail}>If you’re still facing some trouble, write to us at <br /><span>cardrop@ackodrive.com</span></Text>
                  </div>
                </>
              )
            }

            {
              contestState === "POLICY_EXPIRED" && (
                <div className={styles.failureModalContentContainer}>
                  <div className={styles.failureContentExpied}>
                    <Image
                      src={`${assetLoader()}/icons/POLICY_EXPIRED.svg`}
                      alt="POLICY_EXPIRED"
                      width={88}
                      height={88}
                    />
                    <Text className={styles.failureContentHeading}>
                      Policy expired!<br />Looks like it ran out of fuel.
                    </Text>
                    <Text className={styles.failureContentSubHead}>
                      Renew it to keep things running smoothly
                    </Text>
                  </div>
                  <Text className={styles.failureTroubleEmail}>If you’re still facing some trouble, write to us at <br /><span>cardrop@ackodrive.com</span></Text>
                </div>
              )
            }

            <div className={styles.failureModalInner}>
              <Text className={styles.failureModalInnerText}>
                You can still explore offers on brand-new cars.
              </Text>
              <Button
                className={styles.contestButton}
                onClick={handleFailureContest}
              >
                Explore ACKO Drive
              </Button>
            </div>
          </div>
        )
      }

    </div>
  );
};

export default ContestSection;